import { useState } from "react";
import { X, Loader2, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatIDR, onlyDigits } from "@/lib/masterMeta";

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";
const labelCls = "text-xs font-semibold text-slate-500 mb-1 block";

export const METHODS = [["transfer", "Transfer Bank"], ["qris", "QRIS Statis"], ["cash", "Tunai"], ["other", "Lainnya"]];
export const methodLabel = (v) => METHODS.find((m) => m[0] === v)?.[1] || v || "-";
export const PAY_ST = {
  menunggu_verifikasi: ["Menunggu Verifikasi", "bg-amber-50 text-amber-700 border-amber-200"],
  terverifikasi: ["Terverifikasi", "bg-green-50 text-green-700 border-green-200"],
  ditolak: ["Ditolak", "bg-red-50 text-red-700 border-red-200"],
};
export const payBadge = (s) => `px-2 py-0.5 rounded-full text-xs border ${(PAY_ST[s] || PAY_ST.menunggu_verifikasi)[1]}`;
export const payLabel = (s) => (PAY_ST[s] || [s])[0];

export function Modal({ title, onClose, children, max = "max-w-lg", testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className={`bg-white rounded-2xl w-full ${max} my-8 shadow-xl`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}

export function PaymentForm({ invoice, onClose, onSaved }) {
  const outstanding = invoice.outstanding_amount ?? Math.max(0, (invoice.total || 0) - (invoice.paid_amount || 0));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(String(outstanding));
  const [method, setMethod] = useState("transfer");
  const [ref, setRef] = useState("");
  const [notes, setNotes] = useState("");
  const [proof, setProof] = useState("");
  const [proofName, setProofName] = useState("");
  const [err, setErr] = useState("");
  const [dup, setDup] = useState("");
  const [busy, setBusy] = useState(false);
  const amt = parseInt(onlyDigits(amount) || "0", 10);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(f.type)) return toast.error("Hanya JPG, PNG, atau PDF.");
    if (f.size > 3 * 1024 * 1024) return toast.error("Ukuran maksimal 3MB.");
    const r = new FileReader();
    r.onload = () => { setProof(r.result); setProofName(f.name); };
    r.readAsDataURL(f);
  };

  const save = async (force = false) => {
    setErr("");
    if (amt <= 0) return setErr("Nominal harus lebih besar dari nol.");
    if (amt > outstanding) return setErr("Nominal tidak boleh melebihi sisa tagihan.");
    setBusy(true);
    try {
      const { data } = await api.post(`/payments?force=${force}`, { invoice_id: invoice.id, payment_date: date, amount: amt, method, reference_no: ref, notes, proof_file: proof });
      toast.success("Pembayaran dicatat. Menunggu verifikasi.");
      onSaved(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) setDup(typeof d === "string" ? d : d?.message || "Kemungkinan pembayaran duplikat.");
      else setErr(formatApiErrorDetail(d) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Catat Pembayaran" onClose={onClose} testid="payment-form">
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-2">
        <div>Invoice: <b>{invoice.invoice_no}</b> · {invoice.student_name}</div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><span className="text-xs text-slate-400 block">Total Invoice</span>{formatIDR(invoice.total)}</div>
          <div><span className="text-xs text-slate-400 block">Terverifikasi</span>{formatIDR(invoice.paid_amount)}</div>
          <div><span className="text-xs text-slate-400 block">Sisa Tagihan</span><b className="text-[#0f2847]">{formatIDR(outstanding)}</b></div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className={labelCls}>Tanggal Pembayaran</label><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} data-testid="payment-date" /></div>
        <div><label className={labelCls}>Nominal Pembayaran</label><input className={inputCls} value={Number(amt).toLocaleString("id-ID")} onChange={(e) => setAmount(onlyDigits(e.target.value))} data-testid="payment-amount" /></div>
        <div><label className={labelCls}>Metode</label><select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method">{METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <div><label className={labelCls}>No. Referensi (opsional)</label><input className={inputCls} value={ref} onChange={(e) => setRef(e.target.value)} data-testid="payment-reference" /></div>
      </div>
      <div><label className={labelCls}>Catatan (opsional)</label><textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="payment-notes" /></div>
      <div>
        <label className={labelCls}>Bukti Pembayaran (opsional · JPG/PNG/PDF, maks 3MB)</label>
        <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-600 cursor-pointer hover:bg-slate-50">
          <Upload className="h-4 w-4" /> {proofName || "Pilih file…"}
          <input type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" onChange={onFile} data-testid="payment-proof" />
        </label>
      </div>
      <div className="text-right text-sm">Sisa setelah verifikasi: <b>{formatIDR(Math.max(0, outstanding - amt))}</b></div>
      {err && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="payment-error"><AlertTriangle className="h-4 w-4" /> {err}</div>}
      {dup && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="payment-dup-warning"><div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {dup}</div><p className="text-xs mt-1">Periksa kembali. Lanjutkan hanya jika ini benar pembayaran berbeda.</p></div>}
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2.5 rounded-lg">Batal</button>
        {dup && <button onClick={() => { setDup(""); save(true); }} disabled={busy} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-medium px-4 py-2.5 rounded-lg" data-testid="payment-save-force">Tetap Catat</button>}
        <button onClick={() => save(false)} disabled={busy} className="flex-1 bg-[#0f2847] hover:bg-[#17416f] text-white font-medium px-4 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="payment-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Simpan Pembayaran</button>
      </div>
    </Modal>
  );
}
